import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { getSite, listRams, type Site } from '../lib/db';
import { cacheGet } from '../lib/cache';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * RAMS action menu — the "add new" entry point.
 *
 * Three tile buttons (Draft / Blank / Upload) sit at the top. A clickable
 * banner below them — "These are the RAMS for your site" — opens the full
 * list view at /sites/:siteId/rams/list. Kept deliberately spare so the
 * primary action (add new) is the focus.
 *
 * The full grouped list (open/email/remove on each row) + the share modal
 * live in RamsListView.tsx.
 */
export function RamsList() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const [site, setSite] = useState<Site | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    let haveCache = false;

    // Phase 1: cache-first paint. The site object is already in our
    // IndexedDB cache from the offline-support layer, so we can render
    // the page header / banner instantly without waiting on a network
    // round-trip. The RAMS count refreshes from phase 2 (it's a
    // small number, fine for it to update silently a moment later).
    cacheGet<Site>('sites', siteId).then((cached) => {
      if (cancelled) return;
      if (cached) {
        haveCache = true;
        setSite(cached);
        setLoading(false);
      }
    }).catch(() => { /* cache miss is fine */ });

    // Phase 2: fresh network fetch — swaps in latest site + accurate count.
    Promise.all([getSite(siteId), listRams(siteId)])
      .then(([s, list]) => {
        if (cancelled) return;
        setSite(s);
        setCount(list.length);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });

    return () => { cancelled = true; };
  }, [siteId]);

  if (loading) {
    return <AppShell title="RAMS library" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  }
  if (!site) {
    return <AppShell title="RAMS library" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;
  }

  return (
    <AppShell title="RAMS library" back={`/sites/${site.id}`}>
      {error && <div className="banner banner-error">{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          A <strong>RAMS</strong> (Risk Assessment & Method Statement) says how a job
          will be done safely. Pick a ready-made one, write your own from blank, or upload
          one a subbie has sent you — stored against this site, viewable on iPad / phone.
        </p>
      </div>

      <div className="rams-add-grid">
        {/* PL21 (UX review): the old labels — "Draft RAMS" / "Blank RAMS" /
            "Upload RAMS" — read like SaaS jargon. "Draft" especially was a
            trap because to a builder it sounds like "an unfinished one" not
            "a pre-built starting point". Action-led labels now lead, the
            icons are just decoration. */}
        <button
          type="button"
          className="rams-add-card rams-add-card-primary"
          onClick={() => nav(`/sites/${site.id}/rams/presets`)}
        >
          <h3>☰ Pick a ready-made one</h3>
          <p>Pre-built with the hard work done — just check it and make it relevant to your site.</p>
        </button>

        <button
          type="button"
          className="rams-add-card"
          onClick={() => nav(`/sites/${site.id}/rams/preset/blank`)}
        >
          <h3>✎ Write your own</h3>
          <p>Know what you want to write? Start with a blank form and dictate or type the lot.</p>
        </button>

        <button
          type="button"
          className="rams-add-card"
          onClick={() => nav(`/sites/${site.id}/rams/new`)}
        >
          <h3>↑ Upload one I already have</h3>
          <p>Already got one? Drop in a PDF or snap a photo of a printed copy.</p>
        </button>
      </div>

      {count === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No RAMS yet. Pick one of the three options above to add your first.
          </p>
        </div>
      ) : (
        <Link
          to={`/sites/${site.id}/rams/list`}
          className="rams-list-banner rams-list-banner-link"
          aria-label={`View all ${count} RAMS for this site`}
        >
          <div>
            <span className="rams-list-banner-title">These are the RAMS for your site</span>
            <span className="rams-list-banner-meta">{count} on file · tap to view, email or remove</span>
          </div>
          <span className="rams-list-banner-chev" aria-hidden="true">›</span>
        </Link>
      )}

      <div style={{ marginTop: 24 }}>
        <Link to={`/sites/${site.id}`} className="back-link">‹ Back to site</Link>
      </div>
    </AppShell>
  );
}
